import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { AiOutlineClose, AiOutlineDown, AiOutlineUp } from "react-icons/ai";


function SmartOR({ data, activeTab }) {
  const conditions = Array.isArray(data?.storedInfo?.smartQR?.conditions)
    ? data.storedInfo.smartQR.conditions.filter(Boolean) // Remove any null/undefined conditions
    : [];

  const timeZone = Intl.supportedValuesOf("timeZone");
  const [isOpen, setIsOpen] = useState(false);
  const [fromTime, setFromTime] = useState("");
  const [toTime, setToTime] = useState("");
  const [link, setLink] = useState("");
  const [errors, setErrors] = useState({});
  const [smartQR, setSmartQR] = useState({
    conditions: conditions,
    defaultUrl: data?.storedInfo?.smartQR?.defaultUrl ?? "",
  });

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  const filteredMovies = timeZone.filter((tz) =>
    tz.toLowerCase().includes(query.toLowerCase())
  );
  const { register, setValue, watch } = useFormContext();

  const handleSelect = (tz) => {
    setSelected(tz);
    setQuery(tz); // Set input value
    setShowDropdown(false); // Hide dropdown after selection
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!fromTime) newErrors.fromTime = "From Time is required";
    if (!toTime) newErrors.toTime = "To Time is required";
    if (fromTime && toTime && toTime <= fromTime) {
      newErrors.toTime = "To Time must be after From Time";
    }
    if (!link) {
      newErrors.link = "Link is required";
    } else if (
      !/^(?:(?:https?:\/\/)?[^\s"']+\.([^.\s"']{2,}|.{2,5})|[^.\s"']{2,}\.[^.\s"']{2,})?$/.test(
        link
      )
    ) {
      newErrors.link = "Enter a valid URL";
    }

    if (!selected) {
      newErrors.timezone = "Timezone is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    setValue("defaultUrl", data?.storedInfo?.smartQR?.defaultUrl || "");
  }, [setValue, data?.storedInfo?.smartQR?.defaultUrl]);

  const onSubmit = async () => {
    if (!validate()) return;
    const { defaultUrl } = watch();
    const newCondition = {
      parameter: "time",
      from: fromTime,
      to: toTime,
      url: link.toLowerCase(),
      timeZone: selected,
    };

    const updatedSmartQR = {
      conditions: [
        ...(Array.isArray(smartQR.conditions) ? smartQR.conditions : []),
        newCondition,
      ],
      defaultUrl: defaultUrl || smartQR.defaultUrl || "",
    };

    setValue("smartQR", updatedSmartQR, { shouldValidate: true });
    setValue("defaultUrl", updatedSmartQR.defaultUrl);
    setSmartQR(updatedSmartQR);

    setFromTime("");
    setToTime("");
    setLink("");
    setSelected(null);
    setQuery("");
    setErrors({});
  };

  useEffect(() => {
    if (activeTab === "smartQR") {
      setIsOpen(true);
      if (!smartQR?.conditions?.length) {
        setSmartQR({
          conditions: [],
          defaultUrl: "",
        });
      }
    }
  }, [activeTab, smartQR?.conditions?.length]);

  // const handleButtonClick = () => {
  //   setIsOpen(true);
  // };

  const handleClose = () => {
   setFromTime("");
   setToTime("");
   setLink("");
   setSelected(null);
   setQuery("");
   setErrors({});
  };

  const handleRemoveFile = (item, fileType) => {
    if (fileType === "smartQR") {
      const updatedConditions =
        smartQR?.conditions?.filter(
          (condition) =>
            condition && // Add null check for condition
            !(
              condition.parameter === item.parameter &&
              condition.from === item.from &&
              condition.to === item.to &&
              condition.url === item.url &&
              condition.timeZone === item.timeZone
            )
        ) || [];

      const updatedSmartQR = {
        ...smartQR,
        conditions: updatedConditions,
      };

      setSmartQR(updatedSmartQR);
      setValue("smartQR", updatedSmartQR, { shouldValidate: true });
    }
  };

  return (
    <div className="flex flex-col mb-4 sm:mb-6">
      {/* Add Item Modal */}

      <div className="flex items-center justify-between mb-4">
        <p className="text-lg font-bold">Smart QR</p>
      </div>

      {/* Display Saved Items */}
      <div className="space-y-3 mb-6">
        {smartQR?.conditions?.length > 0 ? (
          smartQR?.conditions
            ?.filter((item) => item != null)
            .map((item, index) => (
              <div
                key={item?._id || index}
                className="p-4 bg-white border border-gray-200 gap-3 rounded-2xl shadow-lg flex items-start justify-between"
              >
                <div>
                  <p className="mb-2 text-base font-normal text-gray-700">
                    <strong>Timezone:</strong>{" "}
                    {item?.timeZone || "Not specified"}
                  </p>
                  <p className="mb-2 text-base font-normal text-gray-700">
                    <strong>From Time:</strong> {item?.from || "Not specified"}
                  </p>
                  <p className="mb-2 text-base font-normal text-gray-700">
                    <strong>To Time:</strong> {item?.to || "Not specified"}
                  </p>
                  {item?.url && (
                    <p className="mb-2 text-base font-normal text-gray-700 break-words max-w-[380px] w-full">
                      <strong>Link: &nbsp;</strong>
                      <a
                        href={
                          item?.url?.startsWith("http")
                            ? item.url
                            : `https://${item.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline break-all"
                      >
                        {item.url}
                      </a>
                    </p>
                  )}
                </div>
                <AiOutlineClose
                  className="text-red-500 cursor-pointer text-base"
                  onClick={() => handleRemoveFile(item, "smartQR")}
                />
              </div>
            ))
        ) : (
          <p className="text-gray-500">No items added yet.</p>
        )}
      </div>
      {isOpen && (
        <div className="bg-gray-200 p-4 rounded-lg my-5">
          <h2 className="text-xl font-semibold mb-4">Add Rule</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700">
              Timezone
            </label>{" "}
            <div ref={wrapperRef} className="w-full relative">
              <div className="relative">
                <input
                  type="text"
                  className={`w-full border rounded-md p-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors?.timezone ? "border-red-500" : "border-gray-300"
                  }`}
                  placeholder="Search timezone..."
                  value={query}
                  onFocus={() => setShowDropdown(true)}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowDropdown(true);
                    if (errors?.timezone) {
                      setErrors((prev) => ({ ...prev, timezone: undefined }));
                    }
                  }}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {showDropdown ? <AiOutlineUp /> : <AiOutlineDown />}
                </div>
              </div>
              {showDropdown && (
                <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md max-h-60 overflow-y-auto shadow-lg">
                  {filteredMovies.length === 0 ? (
                    <li className="p-2 text-gray-500">No results found</li>
                  ) : (
                    filteredMovies.map((tz, index) => (
                      <li
                        key={index}
                        className="p-2 cursor-pointer hover:bg-blue-100"
                        onClick={() => handleSelect(tz)}
                      >
                        {tz}
                      </li>
                    ))
                  )}
                </ul>
              )}
              {errors?.timezone && (
                <p className="text-red-500 text-sm mt-2">{errors.timezone}</p>
              )}
            </div>
          </div>
          <div className="mb-4">
            <div className="flex items-center mb-4 space-x-4 justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  From Time
                </label>
                <input
                  type="time"
                  value={fromTime}
                  onChange={(e) => {
                    setFromTime(e.target.value);
                    if (errors?.fromTime) {
                      setErrors((prev) => ({ ...prev, fromTime: undefined }));
                    }
                  }}
                  className={`${
                    errors?.fromTime ? "border-red-500" : "border-gray-400"
                  } w-full px-3 py-2 border  rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0082B2] focus:border-[#0082B2]`}
                />
                {errors.fromTime && (
                  <p className="text-red-500 text-xs mt-1">{errors.fromTime}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  To Time
                </label>
                <input
                  type="time"
                  value={toTime}
                  onChange={(e) => {
                    setToTime(e.target.value);
                    if (errors.toTime) {
                      setErrors((prev) => ({ ...prev, toTime: undefined }));
                    }
                  }}
                  className={`${
                    errors?.toTime ? "border-red-500" : "border-gray-400"
                  } w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0082B2] focus:border-[#0082B2]`}
                />
                {errors?.toTime && (
                  <p className="text-red-500 text-xs mt-1">{errors.toTime}</p>
                )}
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-medium text-gray-700">
                Link
              </label>
              <input
                value={link}
                onChange={(e) => {
                  setLink(e.target.value);
                  if (errors.link) {
                    setErrors((prev) => ({ ...prev, link: undefined }));
                  }
                }}
                placeholder="Put your link here"
                className={`w-full text-sm sm:text-base placeholder-gray-500 px-4 rounded-lg border ${
                  errors?.link ? "border-red-500" : "border-gray-400"
                } w-full py-3 focus:outline-none focus:border-blue-400`}
              />
              {errors?.link && (
                <span className="text-red-500 text-xs mt-1 block">
                  {errors.link}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="mt-4  px-6 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              className="mt-4 bg-tealBlue text-white px-6 py-2 rounded-lg hover:bg-tealBlue"
            >
              Add Rule
            </button>
          </div>
        </div>
      )}
      {/* <div className="flex justify-end">
        <button
          type="button"
          onClick={handleButtonClick}
          className="mt-4 max-w-[150px] w-full bg-tealBlue  text-white px-6 py-2 rounded-lg hover:bg-tealBlue flex items-center gap-2"
        >
          <FaPlusCircle />
          Add Rule
        </button>
      </div> */}
      <div>
        <div className="mt-8">
          <p>Default destination</p>
          <input
            {...register("defaultUrl")}
            placeholder="Enter link here"
            className={`w-full text-sm mt-2 placeholder-gray-500 px-4 rounded-lg border ${
              errors?.defaultUrl ? "border-red-500" : "border-gray-400"
            } w-full py-3 focus:outline-none focus:border-blue-400`}
          />
          {errors?.defaultUrl && (
            <span className="text-red-500 text-xs mt-1 block">
              {errors.defaultUrl.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default SmartOR;
